import Column from '@app/components/layout/Column'
import {
  Children,
  isValidElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { Title } from '@app/components/core/typography/Title'
import { EditButton } from '@app/components/core/button/EditButton'
import Row from '@app/components/layout/Row'
import useMediaQuery from '@app/util/useMediaQuery'
import EditContainerCloseContext from './editContainerCloseContext'

const EditContainerComponent = ({
  title,
  children,
}: {
  title?: string
  children: ReactNode[]
}) => {
  const isMobile = useMediaQuery()
  const [editMode, setEditMode] = useState(false)
  const closeEdit = useCallback(() => setEditMode(false), [])

  const { editChild, viewChild } = useMemo(() => {
    const childrenArray = Children.toArray(children)
    let editChild
    let viewChild

    for (const child of childrenArray) {
      if (isValidElement(child)) {
        if (child.type === EditMode) {
          if (!editChild) {
            editChild = child
          }
          continue
        }
        if (child.type === ViewMode) {
          if (!viewChild) {
            viewChild = child
          }
          continue
        }
      }
    }

    if (!editChild || !viewChild) {
      throw new Error('EditContainer requires View and Edit sections')
    }

    return { editChild, viewChild }
  }, [children])

  if (!title) {
    return (
      <EditContainerCloseContext.Provider value={closeEdit}>
        <Column gap={0} style={{ alignItems: 'stretch' }}>
          {!editMode ? (
            <Row
              style={{ alignItems: 'start', justifyContent: 'space-between' }}
            >
              {viewChild}
              {
                <EditButton
                  isVisible={!isMobile}
                  onClick={() => setEditMode(true)}
                />
              }
            </Row>
          ) : (
            editChild
          )}
        </Column>
      </EditContainerCloseContext.Provider>
    )
  }

  return (
    <EditContainerCloseContext.Provider value={closeEdit}>
      <Column gap={0} style={{ alignItems: 'stretch' }}>
        <Row style={{ alignItems: 'start', justifyContent: 'space-between' }}>
          <Title.H5>{title}</Title.H5>
          {
            <EditButton
              isVisible={!isMobile && !editMode}
              onClick={() => setEditMode(true)}
            />
          }
        </Row>
        {editMode ? editChild : viewChild}
      </Column>
    </EditContainerCloseContext.Provider>
  )
}

const EditMode = ({ children }: { children?: ReactNode }) => {
  return <>{children}</>
}

const ViewMode = ({ children }: { children?: ReactNode }) => {
  return <>{children}</>
}

export const EditContainer = Object.assign(EditContainerComponent, {
  Edit: EditMode,
  View: ViewMode,
})
